/**
 * NL client glue (M5.md). The browser-side counterpart to the server planner:
 * given the /api/nl response and the LOCAL point set, it executes the plan with
 * ZERO model cost — STYLE ops fold onto the current spec (op-stack grows), QUERY
 * runs the deterministic engine over the local points (the answer + map effect
 * are computed here, never by the model). This is the non-visual logic the NL
 * bar UI calls; it is pure and unit-testable without a browser.
 */

import type { MapOp } from "@/lib/contracts/nl";
import type { MapSpec } from "@/lib/map/mapspec";
import type { PointSet } from "@/lib/map/pointset";
import { reduceStack } from "./opstack";
import { runQuery } from "./query";
import { assembleContext } from "./context";
import type { Anchor, LLMContext, MapEffect, QueryPlan } from "./types";
import type { NLIntent } from "./provider";

/** The shape the /api/nl route returns (a superset of the NLResponse contract). */
export interface NLApiResponse {
  intent: NLIntent;
  answer_text: string;
  map_ops: MapOp[];
  executable_query: QueryPlan | null;
  anchor: Anchor | null;
  clarify: { question: string; options: string[] } | null;
  explanation: string;
  confidence: number;
  fallbacks: string[];
  degraded?: boolean;
}

export interface NLApplyResult {
  intent: NLIntent;
  /** Text to show the user: engine-computed for QUERY, the confirmation for STYLE. */
  answerText: string;
  explanation: string;
  fallbacks: string[];
  /** The op stack after applying STYLE ops (unchanged for non-STYLE). */
  ops: MapOp[];
  /** The rendered spec = reduceStack(base, ops). */
  spec: MapSpec;
  /** QUERY map effect (highlight / filter-to / fit / annotate). */
  effect?: MapEffect;
  groups?: Array<{ group: string; value: number; count: number }>;
  /** Exact local rows used by the deterministic query result. */
  matchedRowCount?: number;
  /** The executable query behind a QUERY answer — kept so the anchor can be nudged + re-run locally (AC-4) and the result promoted to a SavedView (AC-10). */
  query?: QueryPlan;
  anchor?: Anchor;
  clarify?: { question: string; options: string[] };
  degraded: boolean;
}

/** Where a QUERY map effect routes on the canvas — the three render seams. */
export interface EffectDispatch {
  /** Ids to light up in the highlight overlay. */
  highlightIds: string[];
  /** Ids to fit/ease the viewport to (only the `fit` effect populates this). */
  fitIds: string[];
  /** On-map annotation badges (group-by aggregates, distance-to labels). */
  annotations: Array<{ id: string; text: string }>;
}

/**
 * Split a QUERY `MapEffect` into the canvas seams. The `fit` effect must ALSO
 * move the camera (it was previously dropped to a plain highlight); `annotate`
 * drives the badge layer, not the highlight overlay. Pure + unit-testable.
 */
export function dispatchEffect(effect: MapEffect | undefined): EffectDispatch {
  const empty: EffectDispatch = { highlightIds: [], fitIds: [], annotations: [] };
  if (!effect || effect.kind === "none") return empty;
  if (effect.kind === "annotate") return { highlightIds: [], fitIds: [], annotations: effect.annotations };
  if (effect.kind === "fit") return { highlightIds: effect.ids, fitIds: effect.ids, annotations: [] };
  // highlight | filter_to
  return { highlightIds: effect.ids, fitIds: [], annotations: [] };
}

export interface NLApplyContext {
  baseSpec: MapSpec;
  opStack: MapOp[];
  pointSet: PointSet;
}

/**
 * Apply an /api/nl response to the local map state. Deterministic and local:
 * QUERY answers/effects are computed here from the point set, so re-running a
 * saved map's ops costs $0 and never calls the model.
 */
export function applyNLResponse(res: NLApiResponse, ctx: NLApplyContext): NLApplyResult {
  const common = {
    intent: res.intent,
    explanation: res.explanation,
    fallbacks: res.fallbacks,
    degraded: Boolean(res.degraded),
    clarify: res.clarify ?? undefined,
    anchor: res.anchor ?? undefined,
  };

  if (res.intent === "STYLE" && res.map_ops.length > 0) {
    const ops = [...ctx.opStack, ...res.map_ops];
    return { ...common, answerText: res.answer_text, ops, spec: reduceStack(ctx.baseSpec, ops) };
  }

  if (res.intent === "QUERY" && res.executable_query) {
    const result = runQuery(res.executable_query, ctx.pointSet.points);
    return {
      ...common,
      answerText: result.answerText,
      ops: ctx.opStack,
      spec: reduceStack(ctx.baseSpec, ctx.opStack),
      effect: result.effect,
      groups: result.groups,
      matchedRowCount: result.matchedIds.length,
      query: res.executable_query,
    };
  }

  // CLARIFY_NEEDED / OUT_OF_SCOPE / degraded: message only, state unchanged.
  return {
    ...common,
    answerText: res.answer_text,
    ops: ctx.opStack,
    spec: reduceStack(ctx.baseSpec, ctx.opStack),
  };
}

/**
 * Re-run a QUERY locally after the user nudges the resolved anchor (AC-4). The
 * plan is already resolved, so this recomputes the answer + effect over the
 * local points with ZERO model cost — the corrected anchor simply changes the
 * spatial math. Returns the same NLApplyResult shape the bar renders.
 */
export function reRunQuery(query: QueryPlan, ctx: NLApplyContext): NLApplyResult {
  const result = runQuery(query, ctx.pointSet.points);
  return {
    intent: "QUERY",
    answerText: result.answerText,
    explanation: "",
    fallbacks: [],
    degraded: false,
    ops: ctx.opStack,
    spec: reduceStack(ctx.baseSpec, ctx.opStack),
    effect: result.effect,
    groups: result.groups,
    matchedRowCount: result.matchedIds.length,
    query,
    anchor: query.spatial?.anchor,
  };
}

/** Build the /api/nl request body (schema-only context — rows never leave). */
export function buildNLRequestBody(
  prompt: string,
  mapId: string,
  pointSet: PointSet,
  opts: { identity?: { userId?: string; anonSessionId?: string }; tier?: "free" | "paid"; requestId?: string } = {},
): { request: { schema_version: 1; request_id: string; map_id: string; user_id?: string; anon_session_id?: string; prompt: string }; context: LLMContext; tier: "free" | "paid" } {
  const context = assembleContext(pointSet);
  const { identity = {}, tier = "free", requestId } = opts;
  return {
    request: {
      schema_version: 1,
      // Caller (UI) should pass a crypto.randomUUID; the fallback is a stable
      // correlation id (near-unique, deterministic — fine for logging/tests).
      request_id: requestId ?? `${mapId}:${prompt.length}:${context.rowCount}`,
      map_id: mapId,
      user_id: identity.userId,
      anon_session_id: identity.anonSessionId,
      prompt,
    },
    context,
    tier,
  };
}
